import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { PrintCashDrawerSlipButton } from '@/components/pos/PrintCashDrawerSlip'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Typography } from '@/components/ui/typography'
import type { AppLinkProps } from '@/hooks/usePathHelper'
import dayjs from '@/lib/dayjs'
import type { CashDrawerReportSession } from '@/lib/services/cash-drawers/cash-drawers.types'
import { formatCurrency, parseCurrency } from '@/lib/utils'
import {
  getCloseBalance,
  formatCashDrawerDifference,
  cashDrawerDifferenceTone,
} from './cash-drawer-report.presentation'

type CashDrawerSessionsTableProps = {
  rows: CashDrawerReportSession[]
  timezone: string
  /** Replaces the table's own count dialog. */
  onSelectSession?: (session: CashDrawerReportSession) => void
  /** Links the date to the session in cash drawer balancing, when the viewer can open it. */
  getBalancingLink?: (session: CashDrawerReportSession) => AppLinkProps | undefined
}

export function CashDrawerSessionsTable({
  rows,
  timezone,
  onSelectSession,
  getBalancingLink,
}: CashDrawerSessionsTableProps) {
  const [selected, setSelected] = useState<CashDrawerReportSession | null>(null)
  const select = onSelectSession ?? setSelected
  if (!rows.length)
    return (
      <Typography variant="supporting" align="center" className="block py-8">
        No cash drawer sessions found.
      </Typography>
    )
  return (
    <>
      <div className="overflow-x-auto rounded-lg border">
        <table aria-label="Counts" className="w-full min-w-[820px]">
          <thead className="bg-muted/40 text-left">
            <tr>
              {['Date / register', 'Counted', 'Expected', 'Over / short', 'Counted by'].map(
                (label) => (
                  <th key={label} className="p-3">
                    <Typography variant="labelStrong">{label}</Typography>
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const balancingLink = getBalancingLink?.(row)
              return (
                <tr key={row.id} className="border-t">
                  <td className="p-3">
                    {balancingLink ? (
                      <Button
                        asChild
                        variant="link"
                        size="inline"
                        className="justify-start text-left"
                      >
                        <Link {...balancingLink}>
                          {formatDateTime(row.closed_at ?? row.opened_at, timezone)}
                        </Link>
                      </Button>
                    ) : (
                      <Typography variant="bodySmall" className="block">
                        {formatDateTime(row.closed_at ?? row.opened_at, timezone)}
                      </Typography>
                    )}
                    <Typography variant="label" className="block">
                      {row.workstation_name}
                    </Typography>
                    <Typography variant="supporting" className="block">
                      {row.location_name}
                    </Typography>
                    <Button variant="link" size="inline" onClick={() => select(row)}>
                      View count
                    </Button>
                  </td>
                  <td className="p-3">
                    <TenderAmounts
                      cash={row.closing_counted_cash}
                      checks={row.closing_counted_checks}
                    />
                  </td>
                  <td className="p-3">
                    <TenderAmounts
                      cash={row.closing_expected_cash}
                      checks={row.closing_expected_checks}
                    />
                  </td>
                  <td className="p-3">
                    {row.closed_at ? (
                      <CloseDifferences row={row} />
                    ) : (
                      <Typography variant="supporting">Not counted yet</Typography>
                    )}
                  </td>
                  <td className="p-3">
                    <Typography variant="bodySmall">
                      {row.closed_at ? row.closed_by || '—' : 'In use'}
                    </Typography>
                    {row.closing_note ? (
                      <Typography
                        variant="caption"
                        className="mt-1 block max-w-56 whitespace-pre-wrap break-words"
                      >
                        {row.closing_note}
                      </Typography>
                    ) : null}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {!onSelectSession ? (
        <CashDrawerSessionDetail
          session={selected}
          timezone={timezone}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </>
  )
}

type TenderAmountsProps = {
  cash: string | null
  checks: string | null
  /** Shows signed over/short amounts instead of plain totals. */
  difference?: boolean
}

function TenderAmounts({ cash, checks, difference = false }: TenderAmountsProps) {
  return (
    <div className="space-y-1 tabular-nums">
      <Typography
        variant="bodySmall"
        tone={difference ? cashDrawerDifferenceTone(cash) : undefined}
      >
        Cash {difference ? formatCashDrawerDifference(cash) : money(cash)}
      </Typography>
      <Typography
        variant="bodySmall"
        tone={difference ? cashDrawerDifferenceTone(checks) : undefined}
      >
        Checks {difference ? formatCashDrawerDifference(checks) : money(checks)}
      </Typography>
    </div>
  )
}

function CloseDifferences({ row }: { row: CashDrawerReportSession }) {
  const balance = getCloseBalance(row)
  return (
    <div className="space-y-1">
      {balance === 'balanced' ? (
        <Badge variant="success">Balanced</Badge>
      ) : (
        <Badge variant={balance === 'different' ? 'warning' : 'secondary'}>
          {balance === 'different' ? 'Difference' : 'Counts unavailable'}
        </Badge>
      )}
      <TenderAmounts
        difference
        cash={row.closing_cash_discrepancy}
        checks={row.closing_check_discrepancy}
      />
    </div>
  )
}

type CashDrawerSessionDetailProps = {
  /** The dialog is open while a session is selected. */
  session: CashDrawerReportSession | null
  timezone: string
  onClose: () => void
}

export function CashDrawerSessionDetail({
  session,
  timezone,
  onClose,
}: CashDrawerSessionDetailProps) {
  return (
    <Dialog
      open={Boolean(session)}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent size="large" viewport="scroll">
        <DialogHeader>
          <DialogTitle>Drawer count</DialogTitle>
          <DialogDescription>
            {session ? `${session.workstation_name} · ${session.location_name}` : ''}
          </DialogDescription>
        </DialogHeader>
        {session ? (
          <div className="space-y-5">
            <div>
              <Typography variant="bodySmall">
                Started {formatDateTime(session.opened_at, timezone)}
              </Typography>
              <Typography variant="supporting">Started by {session.opened_by || '—'}</Typography>
            </div>
            <dl className="grid grid-cols-2 gap-3">
              <DetailAmount label="Starting cash counted" value={session.opening_counted_cash} />
              <DetailAmount label="Starting cash expected" value={session.opening_expected_cash} />
              <DetailAmount label="Cash counted" value={session.closing_counted_cash} />
              <DetailAmount label="Cash expected" value={session.closing_expected_cash} />
              <DetailAmount label="Checks counted" value={session.closing_counted_checks} />
              <DetailAmount label="Checks expected" value={session.closing_expected_checks} />
            </dl>
            {session.closed_at ? (
              <>
                <CloseDifferences row={session} />
                <dl className="grid grid-cols-2 gap-3">
                  <DetailAmount
                    label="Cash left in the drawer"
                    value={session.closing_retained_cash}
                  />
                  <DetailAmount label="Cash to deposit" value={session.closing_deposit_cash} />
                  <DetailAmount label="Checks to deposit" value={session.closing_counted_checks} />
                  <DetailAmount
                    label="Total deposit including checks"
                    value={
                      session.closing_deposit_cash == null || session.closing_counted_checks == null
                        ? null
                        : parseCurrency(session.closing_deposit_cash) +
                          parseCurrency(session.closing_counted_checks)
                    }
                  />
                </dl>
                <Typography variant="bodySmall">
                  Counted {formatDateTime(session.closed_at, timezone)}
                </Typography>
                <Typography variant="supporting" className="block">
                  Counted by {session.closed_by || '—'}
                </Typography>
              </>
            ) : (
              <Badge variant="secondary">Not counted yet</Badge>
            )}
            {session.opening_note ? (
              <SessionNote label="Starting note" note={session.opening_note} />
            ) : null}
            {session.closing_note ? (
              <SessionNote label="Count note" note={session.closing_note} />
            ) : null}
            {session.closed_at ? <PrintCashDrawerSlipButton sessionId={session.id} /> : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function DetailAmount({ label, value }: { label: string; value: number | string | null }) {
  return (
    <div>
      <Typography as="dt" variant="supporting">
        {label}
      </Typography>
      <Typography as="dd" variant="labelStrong" className="tabular-nums">
        {money(value)}
      </Typography>
    </div>
  )
}
function SessionNote({ label, note }: { label: string; note: string }) {
  return (
    <div>
      <Typography variant="labelStrong" className="block">
        {label}
      </Typography>
      <Typography variant="supporting" className="block whitespace-pre-wrap break-words">
        {note}
      </Typography>
    </div>
  )
}
function formatDateTime(value: string, timezone: string) {
  return dayjs(value).tz(timezone).format('MMM D, YYYY h:mm A')
}
function money(value: number | string | null) {
  return value == null ? '—' : formatCurrency(value)
}
